/**
 * The route's words on paper (the Oct 21 canvas folds the separate Delivery
 * Route page into the day cover and the stop sheets): the school's door notes
 * as lines with an icon read off the words, what rides along for a later day,
 * and the route's ONE code (the Oct 20 all-hands: Hunter's phone read the
 * per-stop codes all at once and set off for the middle stop; "do a single for
 * multi-route"). The code opens the route page, every stop by name, tap one to
 * navigate; without a signed link, the whole route in Google Maps. ASSUMED:
 * Matt's open item is to tag door notes by type in Ordo so the icons come from
 * data. Pure; unit-tested.
 */

import { type InstructionItem, instructionItems } from '@/lib/chefs/delivery-route'
import type { Campus } from '@/lib/chefs/types'
import { MEALTIME_LABEL } from '@/lib/chefs/types'

import type { PacketDay } from './day'
import { campusMealSections, serveDayWords } from './meals'
import type { RouteCode, RouteIcon, RouteInstruction } from './units'

/** Keyword rules, first match wins: a check before a door ("Make sure you enter the doors" is a door). */
const ICON_RULES: ReadonlyArray<[RouteIcon, RegExp]> = [
  ['check', /\b(verify|confirm|ensure|accounted for)\b/i],
  ['flame', /\b(heaters?|hot food|warmers?|ovens?|hot box(es)?|cambros?)\b/i],
  ['car', /\b(park(ing|ed)?|lots?|pull(s|ed)? (in|up|into)|drive|driveway|street|road|turn|roundabout|way)\b/i],
  ['door', /\b(doors?|enter|entrance|doorbell|ring|front desk|lobby)\b/i],
  ['phone', /\b(call|phone|text)\b/i],
  ['calendar', /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekdays?)\b/i],
]

function iconFor(text: string): RouteIcon {
  return ICON_RULES.find(([, pattern]) => pattern.test(text))?.[0] ?? 'info'
}

/** A line as a sentence: its own words, with a full stop when it ends without one. */
function sentence(text: string): string {
  const trimmed = text.trim()
  return /[.!?:)]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

function line(icon: RouteIcon, text: string, strong = false): RouteInstruction {
  return { icon, text: sentence(text), strong }
}

/** "Delivery Contact Number" → "contact number": the field's label, lowercase, without "Delivery". */
function fieldKind(label: string): string {
  return label
    .toLowerCase()
    .replace(/\bdelivery\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The school's door notes as the route prints them (Matt, Oct 20), a line
 * each with its icon, a repeated line once:
 * - "Delivery Window: …" is dropped when the stop prints its window already;
 * - "Delivery Contact" and "Delivery Contact Number" read as one line,
 *   "Delivery contacts: Jordan Wallace or Liz Bastian · 704-589-3976";
 * - "Delivery Location: Door #2" reads "Drop off at Door #2.", "Delivery
 *   Days: …" reads "Delivers …";
 * - any other line keeps the school's words.
 */
export function routeInstructions(text: string | null | undefined, hasWindow: boolean): RouteInstruction[] {
  const items = instructionItems(text)
  const fields = items.filter((item): item is Extract<InstructionItem, { kind: 'field' }> => item.kind === 'field')
  const number = fields.find((field) => /\b(number|phone)\b/.test(fieldKind(field.label)))
  const out: RouteInstruction[] = []
  let contactSaid = false
  for (const item of items) {
    if (item.kind !== 'field') {
      out.push(line(iconFor(item.text), item.text, iconFor(item.text) === 'check'))
      continue
    }
    const kind = fieldKind(item.label)
    if (/\bwindow\b/.test(kind)) {
      if (!hasWindow) {
        out.push(line('clock', `Window ${item.value}`))
      }
      continue
    }
    if (item === number) {
      // Said with the contact; on its own line only when no contact names a person.
      if (!fields.some((field) => /\bcontacts?\b/.test(fieldKind(field.label)) && field !== number)) {
        out.push(line('phone', `Delivery contact: ${item.value}`))
      }
      continue
    }
    if (/\bcontacts?\b/.test(kind) && !contactSaid) {
      contactSaid = true
      out.push(line('contact', `Delivery contacts: ${[item.value, number?.value].filter(Boolean).join(' · ')}`))
      continue
    }
    if (/\b(location|drop ?off)\b/.test(kind)) {
      out.push(line('pin', `Drop off at ${item.value}`))
      continue
    }
    if (/\bdays?\b/.test(kind)) {
      out.push(line('calendar', `Delivers ${item.value}`))
      continue
    }
    out.push(line(iconFor(`${item.label} ${item.value}`), `${item.label}: ${item.value}`))
  }
  return out
}

/**
 * What the driver carries for another day at a stop (Matt, Oct 20: "Wed 10/21
 * breakfast rides along: drop it as a separate labeled group."), and a meal
 * that feeds today and a later day. Empty when everything is for today.
 */
export function laterMealWords(campus: Campus, deliveryIso: string): string[] {
  return campusMealSections(campus, deliveryIso).flatMap((section) => {
    const mealtime = MEALTIME_LABEL[section.mealtime].toLowerCase()
    if (section.serve.later && section.serve.laterDay) {
      return [`${serveDayWords(section.serve.laterDay)} ${mealtime} rides along: drop it as a separate labeled group.`]
    }
    if (section.serve.mixed && section.serve.laterDay) {
      return [`The ${mealtime} feeds today and ${serveDayWords(section.serve.laterDay)}: drop it labeled by serve day.`]
    }
    return []
  })
}

/** "25890 Middlebelt Rd., Farmington Hills, MI 48336" → "25890 Middlebelt Rd., Farmington Hills" (Matt, Oct 20). */
export function streetAndCity(address: string): string {
  return address.replace(/,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\s*$/, '').trim()
}

/** "5 stops in driving order". */
export function routeTitle(driven: number): string {
  return `${driven} ${driven === 1 ? 'stop' : 'stops'} in driving order`
}

/** "Leave at 6:00 AM from 25890 Middlebelt Rd., Farmington Hills"; null when neither is known. */
export function routeLeaveWords(leaveBy: string | null, kitchenAddress: string | null): string | null {
  if (kitchenAddress) {
    return `Leave ${leaveBy ? `at ${leaveBy} ` : ''}from ${streetAndCity(kitchenAddress)}`
  }
  return leaveBy ? `Leave at ${leaveBy}` : null
}

/** "5 stops in driving order · Leave at 6:00 AM from 25890 Middlebelt Rd., Farmington Hills": the line under the title. */
export function routeSubtitle(driven: number, leaveBy: string | null, kitchenAddress: string | null): string {
  return [routeTitle(driven), routeLeaveWords(leaveBy, kitchenAddress)].filter(Boolean).join(' · ')
}

/**
 * The route's one code: the day's route page when the read signed a link and the packet knows its origin, else the
 * whole route in Google Maps when one link holds every stop (a longer day's chained links would drop the rest), else
 * none.
 */
export function routeCode(routeDay: PacketDay['routeDay'], origin: string | null): RouteCode | null {
  const path = routeDay?.link?.path
  if (path && origin) {
    return { url: `${origin.replace(/\/+$/, '')}${path}`, opens: 'route-page' }
  }
  const maps = routeDay?.googleMapsUrls ?? []
  return maps.length === 1 ? { url: maps[0]!, opens: 'maps' } : null
}
